import type { DataItemId } from '@pn/core/domain/data';
import {
  parseStackItemId,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { workspaceActions } from '@pn/core/storage';
import { assert } from '@pn/core/utils/assert';
import { apiListProvider } from '@pn/services/api/list/apiListProvider';
import { apiStackProjectDetailsProvider } from '@pn/services/api/stackdx/apiStackProjectDetailsProvider';
import { isEmpty, isNil } from 'lodash-es';

export async function loadItem(
  item: WorkspaceItem,
  companyId: string | undefined
): Promise<WorkspaceItem> {
  if (item.isLoaded) return item;

  if (!isNil(item.query.sourceListId)) {
    return loadReferencedListIds(item, item.query.sourceListId, companyId);
  }

  switch (item.origin) {
    case 'stackdx': {
      const ids = await fetchStackListIds(
        item.id.split('|')[0],
        item.sourceItem!.id,
        companyId
      );
      if (ids.length === 0) {
        console.warn('Stack item with no ids', item);
      }

      workspaceActions().updateQueryProperty(item.id, { scopeIds: ids });
      workspaceActions().markAsLoaded(item.id);

      return {
        ...item,
        query: {
          ...item.query,
          scopeIds: ids,
        },
        isLoaded: true,
      };
    }
    default: {
      assert(item.itemType === 'list', 'Only list items can be loaded');

      const {
        query: { scopeIds },
      } = await apiListProvider.getListWorkspaceItemById(item.id);

      const loadedItem = {
        ...item,
        query: {
          ...item.query,
          scopeIds,
        },
        isLoaded: true,
        isProcessed: true,
      };

      workspaceActions().add(loadedItem);

      return loadedItem;
    }
  }
}

/**
 * Fills `scopeIds` with the referenced list's current ids. They're always
 * fetched rather than read from the workspace store, which may not have the
 * referenced list (e.g. Stack projects still loading).
 */
async function loadReferencedListIds(
  item: WorkspaceItem,
  referencedListId: WorkspaceItem['id'],
  companyId: string | undefined
): Promise<WorkspaceItem> {
  const stackItemId = parseStackItemId(referencedListId);

  const scopeIds = isNil(stackItemId)
    ? (await apiListProvider.getListWorkspaceItemById(referencedListId)).query
        .scopeIds
    : await fetchStackListIds(
        stackItemId.stackProjectId,
        stackItemId.sourceItemId,
        companyId
      );

  /**
   * Empty `scopeIds` mean no constraint at all, which would apply the
   * filters to the entire base layer instead of the list.
   */
  if (isEmpty(scopeIds)) {
    throw new Error(`Referenced list ${referencedListId} has no elements`);
  }

  workspaceActions().updateQueryProperty(item.id, { scopeIds });
  workspaceActions().markAsLoaded(item.id);

  return {
    ...item,
    query: {
      ...item.query,
      scopeIds,
    },
    isLoaded: true,
  };
}

function fetchStackListIds(
  stackProjectId: string,
  sourceItemId: string,
  companyId: string | undefined
): Promise<DataItemId[]> {
  assert(companyId, 'Company ID is required to load Stack projects');

  return apiStackProjectDetailsProvider.getListIds(
    stackProjectId,
    sourceItemId,
    companyId
  );
}
